import { useEffect } from "react";
import type { GridHint, WordGrid, SolvedWord } from "../../../shared/types";

export function GridCluePanel({ clues, solvedWords, onClueClick, highlightedClueId }: { clues: GridHint[]; solvedWords: Record<string, SolvedWord[]>; onClueClick?: (clue: GridHint) => void; highlightedClueId?: string | null }) {
  const across = clues.filter((c) => c.direction === "across").sort((a, b) => a.number - b.number);
  const down = clues.filter((c) => c.direction === "down").sort((a, b) => a.number - b.number);

  // Keep the probed clue visible if its list is scrolled out of view.
  useEffect(() => {
    if (!highlightedClueId) return;
    const els = Array.from(document.querySelectorAll<HTMLElement>(`[data-clue-id="${highlightedClueId}"]`));
    const el = els.find((e) => e.getBoundingClientRect().width > 0);
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [highlightedClueId]);

  function ClueItem({ clue, highlighted }: { clue: GridHint; highlighted: boolean }) {
    const wordId = `${clue.direction}-${clue.number}`;
    const solved = solvedWords[wordId] || [];
    return (
      <li data-clue-id={wordId}>
        <button
          type="button"
          onClick={() => onClueClick?.(clue)}
          className={`w-full -mx-1 px-1 text-left text-sm rounded transition-colors hover:bg-white/5 active:bg-white/10 ${
            highlighted ? "bg-vsw-500/30 text-white ring-1 ring-vsw-400" : solved.length ? "text-green-400" : "text-vsw-300"
          }`}
        >
          <span className="text-vsw-500 font-bold">{clue.number}.</span> {clue.text}
          {solved.length > 0 && (
            <span className="ml-1 text-green-400 text-xs">
              ✓ {solved.map((s) => (s.playerId === "p1" ? "P1" : "P2")).join("+")}
            </span>
          )}
        </button>
      </li>
    );
  }

  return (
    <div className="w-full bg-board-cell border border-board-border rounded-lg p-3 sm:p-4 space-y-3">
      <h3 className="text-vsw-400 font-bold text-sm uppercase tracking-wider">Hints</h3>
      <div className="grid grid-cols-1 gap-4">
        <div>
          <h4 className="text-vsw-200 text-xs font-medium mb-1">Across</h4>
          <ul className="space-y-1 max-h-32 lg:max-h-48 overflow-y-auto text-sm">
            {across.map((c) => <ClueItem key={`${c.direction}-${c.number}`} clue={c} highlighted={highlightedClueId === `${c.direction}-${c.number}`} />)}
          </ul>
        </div>
        <div>
          <h4 className="text-vsw-200 text-xs font-medium mb-1">Down</h4>
          <ul className="space-y-1 max-h-32 lg:max-h-48 overflow-y-auto text-sm">
            {down.map((c) => <ClueItem key={`${c.direction}-${c.number}`} clue={c} highlighted={highlightedClueId === `${c.direction}-${c.number}`} />)}
          </ul>
        </div>
      </div>
    </div>
  );
}

export function ArcadeWordPanel({ puzzle, solvedWords }: { puzzle: WordGrid; solvedWords: Record<string, SolvedWord[]> }) {
  const words = puzzle.targetWords || [];
  if (words.length === 0) return null;

  return (
    <div className="w-full bg-board-cell border border-board-border rounded-lg p-3 sm:p-4 space-y-3">
      <h3 className="text-vsw-400 font-bold text-sm uppercase tracking-wider">Target Words</h3>
      <ul className="space-y-2 max-h-40 lg:max-h-64 overflow-y-auto text-sm">
        {words.map((w, i) => {
          const wordId = w.word.toUpperCase();
          const solved = solvedWords[wordId] || [];
          return (
            <li key={i} className={`${solved.length ? "text-green-400" : "text-vsw-300"}`}>
              <span className="text-vsw-500 font-bold">{i + 1}.</span>{" "}
              {w.clue}
              {solved.length > 0 && (
                <span className="ml-1 text-green-400 text-xs">
                  ✓ {solved.map((s) => (s.playerId === "p1" ? "P1" : "P2")).join("+")}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}